import React, { useState, useRef, useEffect } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import "primeicons/primeicons.css";
import "./N8nChatbot.css";
import localeText from "../../locale";

const N8nChatbot = ({ isConfimeaCardVisible }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: localeText("welcomeMessage"),
      sender: "bot",
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesContainerRef = useRef(null);

  const chatbotUrl =
    "https://n8n.automasell.site/webhook/12daa182-32b3-45fa-a6db-5756f1ebe67c/chat";
  const [webhookStatus, setWebhookStatus] = useState("unknown"); // unknown, online, offline

  const isDevelopment =
    window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

  const getMockResponse = (message) => {
    const responses = {
      olá: "Olá! Bem-vindo à Extraconomy! Como posso ajudá-lo hoje?",
      hello: "Hello! Welcome to Extraconomy! How can I help you today?",
      cadastro:
        "Para se cadastrar na Extraconomy, acesse nossa página de registro e preencha os dados solicitados. Você pode criar uma conta gratuita em poucos minutos!",
      cadastrar:
        "Para se cadastrar na Extraconomy, acesse nossa página de registro e preencha os dados solicitados. Você pode criar uma conta gratuita em poucos minutos!",
      preço:
        "Nossos preços variam conforme o plano escolhido. Temos opções gratuitas e premium. Visite nossa página de planos para mais detalhes!",
      planos:
        "Oferecemos diferentes planos para atender suas necessidades. Consulte nossa página de planos para ver todas as opções disponíveis.",
      ajuda:
        "Estou aqui para ajudar! Posso esclarecer dúvidas sobre cadastro, planos, funcionalidades e muito mais. O que você gostaria de saber?",
      contato:
        "Você pode entrar em contato conosco através do email suporte@extraconomy.com ou através deste chat mesmo!",
      extraconomy:
        "A Extraconomy é uma plataforma inovadora de economia cooperativa e circular. Conectamos pessoas e empresas para criar um ecossistema sustentável.",
      default:
        "Obrigado pela sua mensagem! Estou aqui para ajudar com informações sobre a Extraconomy. Você pode perguntar sobre cadastro, planos, funcionalidades ou qualquer outra dúvida!",
    };

    const lowerMessage = message.toLowerCase();

    for (const [key, response] of Object.entries(responses)) {
      if (lowerMessage.includes(key)) {
        return response;
      }
    }

    return responses.default;
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }, 100);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    scrollToBottom();
  }, [isLoading]);

  useEffect(() => {
    if (isDevelopment) {
      setWebhookStatus("online");
      return;
    }

    const testWebhook = async () => {
      try {
        const response = await fetch(chatbotUrl, {
          method: "HEAD",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.ok || response.status === 405) {
          setWebhookStatus("online");
        } else {
          setWebhookStatus("offline");
        }
      } catch (error) {
        setWebhookStatus("offline");
      }
    };

    testWebhook();
  }, [isDevelopment]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const currentMessage = inputMessage;
    setInputMessage("");
    setIsLoading(true);
    scrollToBottom();

    if (isDevelopment) {
      try {
        const response = await fetch(chatbotUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            chatInput: currentMessage,
            sessionId: `session_${Date.now()}`,
            userId: 'webhook_user'
          }),
        });

        if (response.ok) {
          const data = await response.json();
          const botMessage = {
            id: Date.now() + 1,
            text: data.output || data.message || data.text || data.response || 'Resposta recebida',
            sender: "bot",
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, botMessage]);
          setIsLoading(false);
          scrollToBottom();
          return;
        }
      } catch (error) {
        console.log('Erro na requisição:', error.message);
      }

      setTimeout(() => {
        const mockResponse = getMockResponse(currentMessage);

        const botMessage = {
          id: Date.now() + 1,
          text: mockResponse,
          sender: "bot",
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, botMessage]);
        setIsLoading(false);
        scrollToBottom();
      }, 1000 + Math.random() * 1000);

      return;
    }

    try {
      const response = await fetch(chatbotUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          chatInput: currentMessage,
          sessionId: `session_${Date.now()}`,
          userId: 'webhook_user'
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const botMessage = {
          id: Date.now() + 1,
          text: data.output || data.message || data.text || data.response || localeText("fallbackResponse"),
          sender: "bot",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, botMessage]);
        scrollToBottom();
      } else {
        const errorMessage = {
          id: Date.now() + 1,
          text: localeText("errorGeneric"),
          sender: "bot",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
        scrollToBottom();
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        text: localeText("errorGeneric"),
        sender: "bot",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      scrollToBottom();
    }

    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleChatbot = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="n8n-chatbot-container">
      {/* Botão flutuante para abrir o chatbot */}
      {!isOpen && (
        <Button
          icon="pi pi-comments"
          className={`n8n-chatbot-toggle-button  ${isConfimeaCardVisible ? "bg-white text-gray-800" : "p-button-success"}`}
          onClick={toggleChatbot}
          tooltip={localeText("tooltip")}
          tooltipOptions={{ position: "left" }}
        />
      )}

      {/* Interface do chatbot */}
      {isOpen && (
        <Card className="n8n-chatbot-card">
          <div className="n8n-chatbot-header">
            <div className="flex align-items-center gap-2">
              <i className="pi pi-comments text-white"></i>
              <span className="text-white font-semibold">
                {isDevelopment ? localeText("chatTitleDev") : localeText("chatTitle")}
              </span>
              <div className="flex align-items-center gap-1">
                <div
                  className={`status-indicator ${isDevelopment ? "dev" : webhookStatus}`}
                  title={`Status: ${isDevelopment ? localeText("statusDevMode") : webhookStatus}`}
                ></div>
                <span className="text-xs text-white">
                  {isDevelopment
                    ? localeText("statusDevMode")
                    : webhookStatus === "online"
                    ? localeText("statusOnline")
                    : webhookStatus === "offline"
                    ? localeText("statusOffline")
                    : localeText("statusChecking")}
                </span>
              </div>
            </div>
            <Button
              icon="pi pi-times"
              className="p-button-text p-button-sm text-white"
              onClick={toggleChatbot}
            />
          </div>

          <Divider className="my-2" />

          <div
            ref={messagesContainerRef}
            className="n8n-chatbot-messages"
            style={{
              height: "300px",
              overflowY: "auto",
              padding: "10px"
            }}
          >
            {messages.map((message) => (
              <div
                key={message.id}
                className={`n8n-chatbot-message ${
                  message.sender === "user" ? "user-message" : "bot-message"
                }`}
              >
                <div className="message-content">{message.text}</div>
                <div className="message-timestamp">
                  {message.timestamp.toLocaleTimeString("pt-BR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="n8n-chatbot-message bot-message">
                <div className="message-content">
                  <i className="pi pi-spin pi-spinner"></i> {localeText("typing")}
                </div>
              </div>
            )}
          </div>

          <Divider className="my-2" />

          <div className="n8n-chatbot-input">
            <div className="flex gap-2">
              <InputText
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={localeText("placeholder")}
                className="flex-1"
                disabled={isLoading}
              />
              <Button
                icon="pi pi-send"
                className="p-button-success"
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isLoading}
              />
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default N8nChatbot;
